import React, {
  FC,
  useEffect,
  useRef,
  useState,
  KeyboardEvent,
  ChangeEvent
} from "react";
import { styled } from "@mui/material/styles";
import variableStyles from "../../theme/variable-styles";
import OpenAiService from "../../services/api/openai.service";
import { useTranslation } from "react-i18next";
import { ITokenPayload } from "@/models/user";
import { jwtDecode } from "jwt-decode";
import UploadFileService from "../../services/api/uploadFile.service";
import { toggleMessage } from "../../components/Toast/Toast";
import { envConfig } from "../../config/env.config";
import { IOpenAi, IOpenAiDocx } from "../../models/openai";
import { IAttachmentFile } from "../../models/Task";
import { useApi } from "../../hooks/useApi.hook";
import { IFeaturePermission } from "../../models/user/models.roles";
import RoleService from "../../services/api/role.service";
import {
  FeatureModel,
  OpenAiPermissionModel
} from "../../models/common/models.enum";

type Role = "user" | "assistant";

interface AttachedFile {
  id: string;
  name: string;
  size?: number;
  raw?: File;
}

interface Message {
  id: number;
  role: Role;
  content: string;
}

const ReadDocxAI: FC = () => {
  const [token, setToken] = useState<ITokenPayload>();
  const { t } = useTranslation();
  const features = useApi<IFeaturePermission[]>({ isFetch: true });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode<ITokenPayload>(token);
      setToken(decoded);
    }
  }, []);

  useEffect(() => {
    if (token)
      features.request(RoleService.getFeaturePemissionByUserId(token.userId));
  }, [token]);

  const canEdit = !!features.data?.find(
    f =>
      f.feature === FeatureModel.OpenAi &&
      f.permissions.includes(OpenAiPermissionModel.Edit)
  );

  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      role: "assistant",
      content: t("OpenAiChat.Greeting")
    }
  ]);

  const [input, setInput] = useState<string>("");
  const [pendingFiles, setPendingFiles] = useState<AttachedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const chatBodyRef = useRef<HTMLDivElement | null>(null);
  const [pendingAssistantId, setPendingAssistantId] = useState<number | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false); // Thêm state để quản lý trạng thái loading

  const fetchAllOpenAiDocs = async () => {
    const res = await OpenAiService.getAll();

    if (res.success && Array.isArray(res.data)) {
      const mapped: AttachedFile[] = res.data.map((doc: IOpenAiDocx) => ({
        id: doc.id,
        name: doc.fileName,
        path: doc.path,
        vectorStoreId: doc.vectorStoreId
      }));

      setPendingFiles(mapped);
    } else {
      toggleMessage({
        type: "error",
        message: res.message ?? t("OpenAiChat.UploadError")
      });
    }
  };

  useEffect(() => {
    fetchAllOpenAiDocs();
  }, []);

  // auto scroll xuống cuối khi có tin nhắn mới
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    let dataOpenAi: IOpenAi = {
      message: input.trim()
    };

    // 1) Thêm message user
    const userMsg: Message = {
      id: Date.now(),
      role: "user",
      content: input.trim()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput("");

    // 2) Thêm message “AI đang trả lời…”
    const waitingId = Date.now() + 1;
    const waitingMsg: Message = {
      id: waitingId,
      role: "assistant",
      content: t("OpenAiChat.WaitingResponse")
    };
    setMessages(prev => [...prev, waitingMsg]);
    setPendingAssistantId(waitingId);

    // 3) Gọi API

    const isFeature = !!features.data?.find(
      f =>
        f.feature === FeatureModel.OpenAi &&
        f.permissions.includes(OpenAiPermissionModel.TaskAnalytics)
    );

    const res = await OpenAiService.create(dataOpenAi);

    // const res = isFeature
    //   ? await OpenAiService.createChatTaskAnalytics(dataOpenAi)
    //   : await OpenAiService.create(dataOpenAi);

    if (res.success) {
      // 4) Khi có kết quả → thay thế tin nhắn đang chờ
      setMessages(prev =>
        prev.map(msg =>
          msg.id === waitingId
            ? { ...msg, content: res.data } // thay nội dung
            : msg
        )
      );
      setPendingAssistantId(null);
    } else {
      // Nếu lỗi → đổi message chờ thành lỗi
      setMessages(prev =>
        prev.map(msg =>
          msg.id === waitingId
            ? { ...msg, content: t("OpenAiChat.ErrorResponse") }
            : msg
        )
      );
      setPendingAssistantId(null);

      toggleMessage({
        type: "error",
        message: res.message ?? ""
      });
    }
  };

  const handleUploadfile = async (mapped: AttachedFile[]) => {
    if (mapped.length === 0) return;

    setLoading(true); // Bắt đầu loading khi bắt đầu tải file

    const filesData = new FormData();
    filesData.append("FolderName", "OpenAi_" + (token?.connectString ?? ""));

    mapped.forEach(p => {
      p.raw && filesData.append("Files", p.raw, p.name);
    });

    await UploadFileService.uploadFile(filesData).then(async response => {
      if (response.success && response.data) {
        let dataOpenAi: IOpenAi = {
          files: response.data.map((f: IAttachmentFile) => ({
            name: f.fileName,
            path: envConfig.MEDIA_ENDPOINT + f.path
          }))
        };

        const res = await OpenAiService.uploadfile(dataOpenAi);

        if (!res.success) {
          UploadFileService.deleteFiles(response.data);
          toggleMessage({
            type: "error",
            message: res.message ?? t("Docs.Admin.UploadError")
          });
        } else {
          // NEW: add user message vào chat
          const userMsgContent =
            input.trim() ||
            `Đã tải lên ${pendingFiles.length} file để đọc & phân tích.`;
          const userMessage: Message = {
            id: Date.now(),
            role: "user",
            content: userMsgContent
          };

          setLoading(false); // Dừng loading khi nhận được phản hồi từ server

          fetchAllOpenAiDocs();
        }
      } else {
        toggleMessage({
          type: "error",
          message: t("Docs.Admin.UploadError")
        });
      }
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClickAttach = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    const validFiles = files.filter(file => {
      const ext = file.name.split(".").pop()?.toLowerCase();
      const isValid = ext === "doc" || ext === "docx";
      if (!isValid) {
        toggleMessage({
          type: "error",
          message: t("OpenAiChat.InvalidFile")
        });
      }
      return isValid;
    });

    if (validFiles.length === 0) {
      e.target.value = "";
      return;
    }
    const mapped: AttachedFile[] = validFiles.map(file => ({
      id: `${file.name}-${file.size}-${file.lastModified}`,
      name: file.name,
      size: file.size,
      raw: file
    }));

    //setPendingFiles(prev => [...prev, ...mapped]);
    e.target.value = ""; // reset input để chọn lại cùng file vẫn nhận

    await handleUploadfile(mapped);
  };

  const removePendingFile = async (id: string) => {
    const res = await OpenAiService.deletefile(id);

    if (res.success) {
      toggleMessage({
        type: "success",
        message: "Xóa tệp thành công" //t("Docs.Admin.UploadError")
      });

      setPendingFiles(prev => prev.filter(f => f.id !== id));
    }
  };

  return (
    <>
      {/* Header giống cấu trúc cũ */}
      <Header>
        <HeaderLeft>
          <Avatar>G</Avatar>
          <div>
            <Title>Chat AI Assistant</Title>
          </div>
        </HeaderLeft>
      </Header>

      {/* Wrapper + Container chat */}
      <Wrapper onClick={e => e.stopPropagation()}>
        <Container>
          {/* NEW: chia layout thành 2 cột: chat & sidebar file */}
          <ContentRow>
            <ChatSection>
              {/* Body */}
              <Body ref={chatBodyRef}>
                {messages.map(msg => (
                  <MessageRow key={msg.id} isUser={msg.role === "user"}>
                    {msg.role === "assistant" && <Avatar small>G</Avatar>}
                    <Bubble isUser={msg.role === "user"}>
                      <MessageContent>{msg.content}</MessageContent>
                    </Bubble>
                  </MessageRow>
                ))}
              </Body>

              {/* Footer / Input */}
              <Footer>
                <Input
                  placeholder={t("OpenAiChat.Placeholder")}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                />
                <SendButton
                  type="button"
                  onClick={handleSend}
                  disabled={!input.trim() && pendingFiles.length === 0}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M8.99992 16V6.41407L5.70696 9.70704C5.31643 10.0976 4.68342 10.0976 4.29289 9.70704C3.90237 9.31652 3.90237 8.6835 4.29289 8.29298L9.29289 3.29298L9.36907 3.22462C9.76184 2.90427 10.3408 2.92686 10.707 3.29298L15.707 8.29298L15.7753 8.36915C16.0957 8.76192 16.0731 9.34092 15.707 9.70704C15.3408 10.0732 14.7618 10.0958 14.3691 9.7754L14.2929 9.70704L10.9999 6.41407V16C10.9999 16.5523 10.5522 17 9.99992 17C9.44764 17 8.99992 16.5523 8.99992 16Z"></path>
                  </svg>
                </SendButton>

                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  multiple
                  accept=".doc,.docx" // ✅ CHỈ CHO CHỌN DOC/DOCX
                  onChange={handleFileChange}
                />
              </Footer>
            </ChatSection>

            {/* NEW: Sidebar bên phải quản lý file */}
            {canEdit && (
              <Sidebar>
                <SidebarHeader>
                  <SidebarTitle>{t("OpenAiChat.DocsTitle")}</SidebarTitle>
                  <SidebarDesc>{t("OpenAiChat.DocsDesc")}</SidebarDesc>
                </SidebarHeader>

                <SidebarButtons>
                  <SidebarButton
                    type="button"
                    onClick={handleClickAttach}
                    disabled={loading}
                    primary
                  >
                    {loading
                      ? t("OpenAiChat.Uploading")
                      : t("OpenAiChat.UploadBtn")}
                  </SidebarButton>
                </SidebarButtons>

                <SidebarFilesWrapper>
                  {pendingFiles.length === 0 ? (
                    <SidebarEmpty>{t("OpenAiChat.NoFiles")}</SidebarEmpty>
                  ) : (
                    pendingFiles.map(f => (
                      <SidebarFileItem key={f.id}>
                        <div className="info">
                          <div className="name" title={f.name}>
                            {f.name}
                          </div>
                          {/* <div className="size">{formatFileSize(f.size)}</div> */}
                        </div>
                        <FileRemoveIcon
                          type="button"
                          onClick={() => removePendingFile(f.id)}
                        >
                          ✕
                        </FileRemoveIcon>
                      </SidebarFileItem>
                    ))
                  )}
                </SidebarFilesWrapper>

                <SidebarNote>{t("OpenAiChat.AskHint")}</SidebarNote>
              </Sidebar>
            )}
          </ContentRow>
        </Container>
      </Wrapper>
    </>
  );
};

export default ReadDocxAI;

/* ===========================
   STYLED COMPONENTS BÊN DƯỚI
   =========================== */

export const Wrapper = styled("div")(() => ({
  width: "100%",
  height: "100vh",
  background: "#ffffff",
  display: "flex",
  justifyContent: "center",

  boxSizing: "border-box",
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  padding: "1rem"
}));

export const Container = styled("div")(() => ({
  width: "100%",
  maxWidth: 900,
  height: "100%",
  background: "#fafafa",
  borderRadius: 16,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden"
}));

// NEW: hàng chứa chat + sidebar
export const ContentRow = styled("div")(() => ({
  display: "flex",
  flex: 1,
  minHeight: 0
}));

// NEW: khu chat bên trái
export const ChatSection = styled("div")(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  borderRight: "1px solid #e5e7eb"
}));

// NEW: sidebar bên phải
export const Sidebar = styled("div")(() => ({
  width: 260,
  padding: "12px",
  display: "flex",
  flexDirection: "column",
  gap: 10,
  background: "#f9fafb"
}));

export const Header = styled("div")(() => ({
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between"
}));

export const HeaderLeft = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "10px"
}));

export const Avatar = styled("div")<{ small?: boolean }>(({ small }) => ({
  width: small ? 26 : 32,
  height: small ? 26 : 32,
  background: variableStyles.MainColor,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  fontWeight: 700,
  fontSize: small ? 13 : 16
}));

export const Title = styled("div")(() => ({
  color: "#000000",
  fontWeight: 600,
  fontSize: 14
}));

export const Subtitle = styled("div")(() => ({
  color: "#000000",
  fontSize: 12
}));

export const Body = styled("div")(() => ({
  flex: 1,
  padding: 16,
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: 6
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: 999
  }
}));

export const MessageRow = styled("div")<{ isUser?: boolean }>(({ isUser }) => ({
  display: "flex",
  gap: "10px",
  marginBottom: "12px",
  justifyContent: isUser ? "flex-end" : "flex-start"
}));

export const Bubble = styled("div")<{ isUser?: boolean }>(({ isUser }) => ({
  maxWidth: "70%",
  padding: "10px 12px",
  fontSize: 14,
  lineHeight: 1.5,
  position: "relative",
  borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
  background: isUser ? variableStyles.MainColor : "white",
  border: isUser ? "none" : "1px solid #1f2937",
  color: isUser ? "#ffffff" : "#000000",
  marginLeft: isUser ? 40 : 0,
  marginRight: isUser ? 0 : 40
}));

export const MessageContent = styled("div")(() => ({
  whiteSpace: "pre-wrap"
}));

export const Footer = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 12px"
}));

export const Input = styled("textarea")(() => ({
  flex: 1,
  resize: "none",
  padding: "8px 10px",
  borderRadius: 10,
  border: "1px solid #e3dddd",
  color: "#000000",
  fontSize: 14,
  maxHeight: 120,
  lineHeight: 1.4,
  "&:focus": { outline: "none" },
  "&::placeholder": {
    color: "#6b7280"
  }
}));

export const SendButton = styled("button")(() => ({
  borderRadius: 999,
  cursor: "pointer",
  width: 34,
  height: 34,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: variableStyles.MainColor,
  color: "#ffffff",
  fontSize: 16,
  border: "none",
  flexShrink: 0,
  "&:disabled": {
    opacity: 0.4,
    cursor: "default"
  }
}));

// NEW: các style cho sidebar
export const SidebarHeader = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 4
}));

export const SidebarTitle = styled("div")(() => ({
  fontSize: 13,
  fontWeight: 600,
  color: "#111827"
}));

export const SidebarDesc = styled("div")(() => ({
  fontSize: 12,
  color: "#6b7280"
}));

export const SidebarButtons = styled("div")(() => ({
  display: "flex",
  gap: 8,
  marginTop: 4
}));

export const SidebarButton = styled("button")<{
  primary?: boolean;
}>(({ primary }) => ({
  flex: 1,
  padding: "6px 8px",
  fontSize: 12,
  borderRadius: 999,
  border: primary ? "none" : "1px solid #d1d5db",
  background: primary ? variableStyles.MainColor : "#ffffff",
  color: primary ? "#ffffff" : "#111827",
  cursor: "pointer",
  fontWeight: 500,
  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed"
  }
}));

export const SidebarFilesWrapper = styled("div")(() => ({
  marginTop: 10,
  flex: 1,
  overflowY: "auto",
  borderRadius: 8,
  border: "1px dashed #e5e7eb",
  padding: 8,
  background: "#ffffff",
  "&::-webkit-scrollbar": {
    width: 4
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: 999
  }
}));

export const SidebarEmpty = styled("div")(() => ({
  fontSize: 12,
  color: "#9ca3af",
  textAlign: "center"
}));

export const SidebarFileItem = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "6px 8px",
  borderRadius: 6,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  fontSize: 12,
  ".info": {
    display: "flex",
    flexDirection: "column",
    maxWidth: "180px"
  },
  ".name": {
    fontWeight: 500,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  ".size": {
    color: "#6b7280",
    fontSize: 11
  }
}));

export const FileRemoveIcon = styled("button")(() => ({
  border: "none",
  background: "transparent",
  color: "#9ca3af",
  cursor: "pointer",
  fontSize: 14,
  padding: 0,
  "&:hover": {
    color: "#ef4444"
  }
}));

export const SidebarNote = styled("div")(() => ({
  marginTop: 8,
  fontSize: 11,
  color: "#6b7280",
  lineHeight: 1.4
}));
